import { isReportingScreenEnabled } from '@/constants/userManagement/reportingScreens';
import { isRestrictedAccessRole, isSuperAdminRole } from '@/helpers/roles';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { type Control, Controller, type FieldValues } from 'react-hook-form';

/**
 * Per-feature visibility overrides nested under Unified Reporting in Access
 * Management (flank#4442, flank#4446, flank#4644, flank#4648) — built to close
 * the gap those four tickets left open: the backend has read `User.canView*`
 * since each shipped, but nothing anywhere let a Ribeye Admin actually set
 * them. "Only Ribeye Admin users can change them. Everyone else sees them
 * read-only" (#4442's own spec) — `canEdit` below is that gate.
 *
 * Three states, not a checkbox: each field is `boolean | null` on the backend
 * — null resolves to the role default (see reportingPermissions.ts /
 * rawInventory.ts / attribution.ts / clientSpend.ts), true/false override it
 * either way. A plain checkbox can't represent "no override" without
 * silently collapsing it to one side.
 */
const OPTIONS = [
  { value: 'default', label: 'Default (role-based)' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
] as const;

export const toOption = (value: boolean | null | undefined): (typeof OPTIONS)[number]['value'] =>
  value === true ? 'on' : value === false ? 'off' : 'default';

export const fromOption = (option: string): boolean | null =>
  option === 'on' ? true : option === 'off' ? false : null;

// A Ribeye Admin target always sees DSP/Platform, our Spend/CPM and Client
// Spend/CPM — "always, no configuration" per #4442/#4446 — so none of the
// three are offered as overrides at all. DSP/Platform was missing from this
// list (preprod QA, 2026-10): resolveReportingPermissions hardcodes
// canViewDspPlatform: true for SUPER_ADMIN exactly like the other two, but
// the toggle still rendered as editable, letting a Ribeye Admin viewer set an
// override that the backend would silently ignore.
const SUPER_ADMIN_LOCKED_FIELDS = ['canViewDspPlatform', 'canViewWholesaleSpendCpm', 'canViewClientSpendCpm'];

// Client / Agency / Advertiser targets are "never, no override" per #4444's grid —
// unlike Org Admin / Whitelabel Admin, where these two are off-by-default but
// overridable — so they're removed from the grid entirely for these targets.
export const RESTRICTED_TIER_NEVER_FIELDS = ['canViewDspPlatform', 'canViewWholesaleSpendCpm'];

export const isOverrideVisibleForTarget = (name: string, userRole: number): boolean => {
  if (isSuperAdminRole(userRole) && SUPER_ADMIN_LOCKED_FIELDS.includes(name)) return false;
  if (isRestrictedAccessRole(userRole) && RESTRICTED_TIER_NEVER_FIELDS.includes(name)) return false;
  return true;
};

/**
 * Whether the whole expanded sub-options area renders at all — gated on the
 * ACTING admin's own role (meRole), never the target's, per live product
 * review 2026-10-09: only a Ribeye/Super Admin viewer may see or edit these
 * five controls, regardless of whose profile is open. An Org Admin or
 * Whitelabel Admin viewer never sees this area, even editing another Org
 * Admin / Whitelabel Admin — they still see and can edit the main Unified
 * Reporting toggle itself, which this gate does not touch. Per-field
 * suppression for the target's own role (isOverrideVisibleForTarget, above)
 * still applies on top of this once the area is visible.
 */
export const isReportingV2SubTogglesVisible = (meRole: number, reportingV2Access: unknown): boolean =>
  isSuperAdminRole(meRole) && isReportingScreenEnabled(reportingV2Access);

interface ToggleProps {
  name: string;
  label: string;
  control: Control<FieldValues, any, FieldValues>;
  canEdit: boolean;
}

// This panel is nested inside Edit Profile's own MUI Modal, so its Select menus are
// a Popover stacked on top of another Modal — the same situation MultiSelect.jsx and
// Forecaster/VenueType/index.tsx already solve by pinning a z-index above the parent
// Modal instead of trusting the default, which rendered these five menus see-through
// and overlapping the controls underneath them (live QA).
const SUB_TOGGLE_MENU_PROPS = {
  sx: { zIndex: (theme: Theme) => theme.zIndex.modal + 1 },
  slotProps: { paper: { sx: { backgroundColor: 'background.paper', opacity: 1 } } },
};

const OverrideToggle = ({ name, label, control, canEdit }: ToggleProps) => (
  <FormControl size='small' sx={{ minWidth: 220 }}>
    <InputLabel id={`${name}-label`}>{label}</InputLabel>
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      render={({ field }) => (
        <Select
          labelId={`${name}-label`}
          label={label}
          value={toOption(field.value)}
          onChange={e => field.onChange(fromOption(e.target.value))}
          disabled={!canEdit}
          MenuProps={SUB_TOGGLE_MENU_PROPS}
        >
          {OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}
    />
  </FormControl>
);

interface IProps {
  control: Control<FieldValues, any, FieldValues>;
  /** Only a Ribeye Admin viewer may change these — everyone else sees the same controls disabled. */
  canEdit: boolean;
  /** The profile being edited — a Ribeye Admin target always sees our Spend/CPM and Client Spend/CPM, so those two are not offered as overrides (#4442, #4446). */
  userRole: number;
}

const ReportingV2SubToggles = ({ control, canEdit, userRole }: IProps) => {
  // No reset-on-role-switch effect here on purpose: getUserPayload already nulls
  // these fields at submit time for restricted roles, so the data-safety concern
  // is covered. An effect doing the same thing live wiped a value the moment the
  // User Type dropdown moved off and back onto a role that hides the field,
  // discarding what the admin had set instead of just not submitting it
  // (confirmed live, Oct 2026: Org Admin -> Client -> Org Admin lost DSP/Platform
  // and wholesale Spend/CPM overrides).

  return (
    <div className='ml-6 mt-2 flex flex-col gap-2 border-l-2 border-gray-200 pl-4'>
      {isOverrideVisibleForTarget('canViewDspPlatform', userRole) && (
        <OverrideToggle name='canViewDspPlatform' label='Show DSP / Platform' control={control} canEdit={canEdit} />
      )}
      {isOverrideVisibleForTarget('canViewWholesaleSpendCpm', userRole) && (
        <OverrideToggle
          name='canViewWholesaleSpendCpm'
          label='Show wholesale Spend and CPM'
          control={control}
          canEdit={canEdit}
        />
      )}
      {isOverrideVisibleForTarget('canViewClientSpendCpm', userRole) && (
        <OverrideToggle
          name='canViewClientSpendCpm'
          label='Show Client Spend and CPM'
          control={control}
          canEdit={canEdit}
        />
      )}
      <OverrideToggle
        name='canViewRawInventory'
        label='Show raw inventory (apps & domains)'
        control={control}
        canEdit={canEdit}
      />
      <OverrideToggle
        name='canViewAttribution'
        label='Show Attribution (Conversions)'
        control={control}
        canEdit={canEdit}
      />
    </div>
  );
};

export default ReportingV2SubToggles;
